import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  canSealItem,
  isLockedFolder,
  isLockedItem,
  isSealedItem,
  itemLock,
  makeId,
  SEALED_ASSET_MIME_TYPE,
  sealedItemSecrets,
  sealLockedProps,
  type CanvasItem,
  type ItemProps,
  type NoteLock
} from "@bedrock/domain";
import type { LocalStore } from "@bedrock/local-store";
import { withoutCanvasAssetUrl } from "./asset-hydration";
import { createItemKey, lockFolder as createFolderLock, openAsset, openItemKey, openPayload, sealAsset, sealPayload, unlockFolder, type FolderKey, type ItemKey, type SealedAssetHeader } from "./note-lock";

type SaveItemChanges = (item: CanvasItem, patch: { props: ItemProps }, options?: { removeLock?: boolean }) => Promise<void>;
type UploadAsset = (input: { assetId: string; filename: string; mimeType: string; blob: Blob }) => Promise<void>;

export const WRONG_PIN_MESSAGE = "That PIN didn't work. Try again.";

/** A sealed item decrypted for the open folder it belongs to. */
interface RevealedItem {
  folderId: string;
  /** The ciphertext this was decrypted from, so remote edits are noticed. */
  lockIv: string;
  itemKey: ItemKey;
  props: ItemProps;
  asset?: SealedAssetHeader;
  url?: string;
}

export interface FolderLocks {
  /** Folders shown open on this device: unlocked locked folders and plain folders opened here. */
  openFolderIds: ReadonlySet<string>;
  /** Shows open folders expanded and their contents decrypted; everything else as stored. */
  display: (item: CanvasItem) => CanvasItem;
  storedItem: (itemId: string) => CanvasItem | undefined;
  openFolder: (folder: CanvasItem, pin: string) => Promise<string | null>;
  /** Shows a folder without a lock as open. Like a PIN session, this stays on this device. */
  expandFolder: (folderId: string) => void;
  closeFolder: (folderId: string) => Promise<void>;
  lockFolder: (folder: CanvasItem, pin: string) => Promise<string | null>;
  removeFolderLock: (folder: CanvasItem, pin: string) => Promise<string | null>;
  /** Re-encrypts edited props of an item decrypted from an open folder. */
  resealForSave: (current: CanvasItem, props: ItemProps) => Promise<ItemProps | null>;
}

function assetIdOf(props: ItemProps): string | null {
  if (props.kind === "image" || props.kind === "video" || props.kind === "pdf" || props.kind === "spreadsheet") return props.assetId;
  if (props.kind === "web-clip") return props.previewAssetId;
  return null;
}

function withAssetId<T extends ItemProps>(props: T, assetId: string): T {
  if (props.kind === "web-clip") return { ...props, previewAssetId: assetId };
  return { ...props, assetId };
}

function withObjectUrl(props: ItemProps, url: string | undefined): ItemProps {
  if (!url) return props;
  if (props.kind === "web-clip") return { ...props, previewUrl: url };
  if (props.kind === "image" || props.kind === "video" || props.kind === "pdf" || props.kind === "spreadsheet") return { ...props, localUrl: url };
  return props;
}

function storedProps(props: ItemProps): ItemProps {
  return withoutCanvasAssetUrl({ props } as CanvasItem).props;
}

function withoutLock(props: ItemProps): ItemProps {
  const { lock: _lock, ...rest } = props as ItemProps & { lock?: unknown };
  return rest;
}

/** Why a folder's contents can't be sealed yet, if any of them are still in transit. */
export function folderSealBlocker(children: CanvasItem[]): string | null {
  if (children.some((child) => child.props.kind === "web-clip" && child.props.status === "pending" && !isLockedItem(child))) return "Wait for web clips in this folder to finish capturing.";
  if (children.some((child) => "status" in child.props && child.props.status === "uploading" && !isLockedItem(child))) return "Wait for files in this folder to finish uploading.";
  return null;
}

export function useFolderLocks({ store, items, saveItemChanges, uploadAsset, downloadAsset }: {
  store: LocalStore;
  items: CanvasItem[];
  saveItemChanges: SaveItemChanges;
  uploadAsset: UploadAsset | undefined;
  downloadAsset: ((assetId: string) => Promise<Blob>) | undefined;
}): FolderLocks {
  const [keys, setKeys] = useState<ReadonlyMap<string, FolderKey>>(new Map());
  const [revealed, setRevealed] = useState<ReadonlyMap<string, RevealedItem>>(new Map());
  // Open and closed is a view of this device, so opening a folder without a lock stores nothing.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const keysRef = useRef(keys);
  keysRef.current = keys;
  const revealedRef = useRef(revealed);
  revealedRef.current = revealed;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const inFlight = useRef(new Map<string, Promise<void>>());
  // Folders whose lock is being removed; their contents must not be sealed again meanwhile.
  const releasing = useRef(new Set<string>());
  // Items that couldn't be sealed (offline, missing file) wait a little before another try.
  const sealRetryAt = useRef(new Map<string, number>());

  const putRevealed = useCallback((itemId: string, entry: RevealedItem | null) => {
    const previous = revealedRef.current.get(itemId);
    if (previous?.url && previous.url !== entry?.url) URL.revokeObjectURL(previous.url);
    const next = new Map(revealedRef.current);
    if (entry) next.set(itemId, entry);
    else next.delete(itemId);
    revealedRef.current = next;
    setRevealed(next);
  }, []);

  const readAsset = useCallback(async (assetId: string): Promise<Blob> => {
    const cached = await store.getCachedAsset(assetId);
    if (cached) return cached.blob;
    if (!downloadAsset) throw new Error("A file in this folder isn't on this device.");
    const blob = await downloadAsset(assetId);
    await store.putCachedAsset({ assetId, blob, byteLength: blob.size, lastAccessedAt: Date.now(), pinned: false });
    return blob;
  }, [downloadAsset, store]);

  /** Stores a replacement file before the item points at it, so the old copy is never the only one lost. */
  const storeReplacement = useCallback(async (blob: Blob, filename: string, mimeType: string): Promise<{ assetId: string; status: "ready" | "local" }> => {
    const assetId = makeId("asset");
    await store.putCachedAsset({ assetId, blob, byteLength: blob.size, lastAccessedAt: Date.now(), pinned: true });
    if (!uploadAsset) return { assetId, status: "local" };
    await uploadAsset({ assetId, filename, mimeType, blob });
    await store.putCachedAsset({ assetId, blob, byteLength: blob.size, lastAccessedAt: Date.now(), pinned: false });
    return { assetId, status: "ready" };
  }, [store, uploadAsset]);

  const forgetCachedAsset = useCallback(async (assetId: string | null, exceptItemId: string) => {
    if (!assetId || itemsRef.current.some((item) => item.id !== exceptItemId && assetIdOf(item.props) === assetId)) return;
    await store.deleteCachedAsset(assetId);
  }, [store]);

  const reveal = useCallback(async (item: CanvasItem, folderId: string, folder: FolderKey) => {
    const lock = itemLock(item.props);
    if (!lock) return;
    const itemKey = await openItemKey(folder, lock);
    const payload = itemKey ? await openPayload(itemKey, lock) : null;
    if (!itemKey || !payload) return;
    let props = { ...withoutLock(storedProps(item.props)), ...payload.props };
    let url: string | undefined;
    const assetId = assetIdOf(item.props);
    if (payload.asset && assetId) {
      try {
        url = URL.createObjectURL(await openAsset(itemKey, await readAsset(assetId), payload.asset));
      } catch {
        // The card still opens with its details; the file shows once it can be fetched.
      }
    }
    props = withObjectUrl(props, url);
    if (!keysRef.current.has(folderId)) {
      if (url) URL.revokeObjectURL(url);
      return;
    }
    putRevealed(item.id, { folderId, lockIv: lock.iv, itemKey, props, ...(payload.asset ? { asset: payload.asset } : {}), ...(url ? { url } : {}) });
  }, [putRevealed, readAsset]);

  /** Seals with the folder's public key, so it works whether or not the folder is open. */
  const seal = useCallback(async (item: CanvasItem, folderId: string, folderLock: NoteLock) => {
    const itemKey = await createItemKey(folderLock);
    const props = storedProps(item.props);
    const previousAssetId = assetIdOf(props);
    let next: ItemProps = props;
    let asset: SealedAssetHeader | undefined;
    let plainUrl: string | undefined;
    if (previousAssetId) {
      const mimeType = "mimeType" in props ? props.mimeType : "image/webp";
      const plain = new Blob([await readAsset(previousAssetId)], { type: mimeType });
      const sealed = await sealAsset(itemKey, plain);
      const replacement = await storeReplacement(sealed.blob, "sealed", SEALED_ASSET_MIME_TYPE);
      next = withAssetId(next, replacement.assetId);
      if ("status" in next && next.kind !== "web-clip") next = { ...next, status: replacement.status };
      asset = sealed.header;
      if (keysRef.current.has(folderId)) plainUrl = URL.createObjectURL(plain);
    }
    const lock = await sealPayload(itemKey, { props: sealedItemSecrets(props), ...(asset ? { asset } : {}) });
    if (keysRef.current.has(folderId)) putRevealed(item.id, { folderId, lockIv: lock.iv, itemKey, props: withObjectUrl(next, plainUrl), ...(asset ? { asset } : {}), ...(plainUrl ? { url: plainUrl } : {}) });
    await saveItemChanges(item, { props: sealLockedProps({ ...next, lock } as ItemProps) });
    await forgetCachedAsset(previousAssetId, item.id);
  }, [forgetCachedAsset, putRevealed, readAsset, saveItemChanges, storeReplacement]);

  const unseal = useCallback(async (item: CanvasItem, folder: FolderKey) => {
    const lock = itemLock(item.props);
    if (!lock) return;
    const entry = revealedRef.current.get(item.id);
    const itemKey = entry?.itemKey ?? await openItemKey(folder, lock);
    const payload = entry ? { props: entry.props as Record<string, unknown>, asset: entry.asset } : itemKey ? await openPayload(itemKey, lock) : null;
    if (!itemKey || !payload) throw new Error("Something in this folder couldn't be decrypted.");
    let next = storedProps({ ...withoutLock(storedProps(item.props)), ...payload.props });
    const sealedAssetId = assetIdOf(item.props);
    if (payload.asset && sealedAssetId) {
      const plain = await openAsset(itemKey, await readAsset(sealedAssetId), payload.asset);
      const filename = "filename" in next ? next.filename : "preview.webp";
      const replacement = await storeReplacement(plain, filename, plain.type || "application/octet-stream");
      next = withAssetId(next, replacement.assetId);
      if ("status" in next && next.kind !== "web-clip") next = { ...next, status: replacement.status };
    }
    await saveItemChanges(item, { props: next }, { removeLock: true });
    putRevealed(item.id, null);
    await forgetCachedAsset(sealedAssetId, item.id);
  }, [forgetCachedAsset, putRevealed, readAsset, saveItemChanges, storeReplacement]);

  /** Runs one change per item at a time; overlapping requests for the same item are skipped. */
  const once = useCallback(async (itemId: string, task: () => Promise<void>) => {
    if (inFlight.current.has(itemId)) return;
    const running = task().finally(() => inFlight.current.delete(itemId));
    inFlight.current.set(itemId, running.catch(() => undefined));
    await running;
  }, []);

  /** Waits for whatever is already running for these items to finish, successfully or not. */
  const settle = useCallback(async (itemIds: string[]) => {
    await Promise.all(itemIds.map((itemId) => inFlight.current.get(itemId) ?? Promise.resolve()));
  }, []);

  const endSession = useCallback((folderId: string) => {
    for (const [itemId, entry] of revealedRef.current) if (entry.folderId === folderId) putRevealed(itemId, null);
    const next = new Map(keysRef.current);
    next.delete(folderId);
    keysRef.current = next;
    setKeys(next);
    setExpanded((current) => {
      if (!current.has(folderId)) return current;
      const remaining = new Set(current);
      remaining.delete(folderId);
      return remaining;
    });
  }, [putRevealed]);

  const expandFolder = useCallback((folderId: string) => {
    setExpanded((current) => current.has(folderId) ? current : new Set(current).add(folderId));
  }, []);

  // Keeps locked folders consistent: seal whatever lands in one (open or closed), decrypt what
  // arrives in an open one, and unseal what is taken out of an open one.
  useEffect(() => {
    const byId = new Map(items.map((item) => [item.id, item]));
    // A released folder stays paused until this list shows it unlocked; sealing sooner would use a
    // key that is about to be deleted and leave the item unreadable.
    for (const folderId of releasing.current) {
      const folder = byId.get(folderId);
      if (!folder || !isLockedFolder(folder)) releasing.current.delete(folderId);
    }
    for (const [folderId] of keys) {
      const folder = byId.get(folderId);
      if (!folder || folder.deletedAt !== null || !isLockedFolder(folder)) endSession(folderId);
    }
    for (const item of items) {
      if (item.deletedAt !== null) continue;
      const parent = item.parentStackId ? byId.get(item.parentStackId) : undefined;
      const parentLock = parent && parent.deletedAt === null && isLockedFolder(parent) ? itemLock(parent.props) : undefined;
      const lock = itemLock(item.props);
      const entry = revealed.get(item.id);
      const session = parent ? keys.get(parent.id) : undefined;
      if (parent && parentLock) {
        if (session && isSealedItem(item) && lock?.salt === parentLock.salt && entry?.lockIv !== lock.iv) {
          void once(item.id, () => reveal(item, parent.id, session));
        } else if (!lock && canSealItem(item) && !folderSealBlocker([item]) && !releasing.current.has(parent.id) && (sealRetryAt.current.get(item.id) ?? 0) <= Date.now()) {
          void once(item.id, () => seal(item, parent.id, parentLock).then(() => { sealRetryAt.current.delete(item.id); }, () => { sealRetryAt.current.set(item.id, Date.now() + 30_000); }));
        }
      } else if (entry && item.parentStackId !== entry.folderId) {
        const origin = keys.get(entry.folderId);
        if (origin) void once(item.id, () => unseal(item, origin).catch(() => undefined));
      }
    }
  }, [endSession, items, keys, once, reveal, revealed, seal, unseal]);

  useEffect(() => () => {
    for (const entry of revealedRef.current.values()) if (entry.url) URL.revokeObjectURL(entry.url);
  }, []);

  const childrenOf = (folderId: string) => itemsRef.current.filter((item) => item.parentStackId === folderId && item.deletedAt === null);

  const openFolder = useCallback(async (folder: CanvasItem, pin: string) => {
    const lock = itemLock(folder.props);
    if (!lock) return null;
    const key = await unlockFolder(pin, lock);
    if (!key) return WRONG_PIN_MESSAGE;
    const next = new Map(keysRef.current).set(folder.id, key);
    keysRef.current = next;
    setKeys(next);
    return null;
  }, []);

  // Nothing needs sealing on the way out: additions are sealed as soon as they land.
  const closeFolder = useCallback(async (folderId: string) => {
    endSession(folderId);
  }, [endSession]);

  const lockFolder = useCallback(async (folder: CanvasItem, pin: string) => {
    if (folder.props.kind !== "stack") return null;
    const children = childrenOf(folder.id);
    const blocker = folderSealBlocker(children);
    if (blocker) return blocker;
    const key = await createFolderLock(pin);
    // The folder is locked first so a partial failure still leaves its contents behind a PIN.
    await saveItemChanges(folder, { props: { ...folder.props, collapsed: true, lock: key.lock } });
    endSession(folder.id);
    let failed = 0;
    for (const child of children) {
      if (isLockedItem(child) || !canSealItem(child)) continue;
      await once(child.id, () => seal(child, folder.id, key.lock)).catch(() => { failed += 1; });
    }
    return failed === 0 ? null : `${failed === 1 ? "One item" : `${failed} items`} couldn't be encrypted yet. Open the folder later to finish.`;
  }, [endSession, once, saveItemChanges, seal]);

  const removeFolderLock = useCallback(async (folder: CanvasItem, pin: string) => {
    const lock = itemLock(folder.props);
    if (folder.props.kind !== "stack" || !lock) return null;
    const key = await unlockFolder(pin, lock);
    if (!key) return WRONG_PIN_MESSAGE;
    const wasOpen = keysRef.current.has(folder.id);
    releasing.current.add(folder.id);
    const failure = "Something in this folder couldn't be decrypted. Try again when you're online.";
    // Decrypt from what is stored, and drop the folder's key only once nothing still needs it.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const inside = (stored: CanvasItem[]) => stored.filter((child) => child.parentStackId === folder.id && child.deletedAt === null);
      await settle(inside(await store.listItems(folder.spaceId)).map((child) => child.id));
      const sealed = inside(await store.listItems(folder.spaceId)).filter((child) => isSealedItem(child) && itemLock(child.props)?.salt === lock.salt);
      if (sealed.length === 0) {
        await saveItemChanges(folder, { props: withoutLock(folder.props) }, { removeLock: true });
        endSession(folder.id);
        // Without a lock the folder is no longer shown through its PIN session, so keep it open as it was.
        if (wasOpen) expandFolder(folder.id);
        return null;
      }
      for (const child of sealed) {
        try {
          await once(child.id, () => unseal(child, key));
        } catch {
          releasing.current.delete(folder.id);
          return failure;
        }
      }
    }
    releasing.current.delete(folder.id);
    return failure;
  }, [endSession, expandFolder, once, saveItemChanges, settle, store, unseal]);

  const resealForSave = useCallback(async (current: CanvasItem, props: ItemProps) => {
    if (isLockedItem({ props })) return null;
    const entry = revealedRef.current.get(current.id);
    if (!entry || !keysRef.current.has(entry.folderId)) return null;
    const lock = await sealPayload(entry.itemKey, { props: sealedItemSecrets(props), ...(entry.asset ? { asset: entry.asset } : {}) });
    putRevealed(current.id, { ...entry, lockIv: lock.iv, props: withObjectUrl(storedProps(props), entry.url) });
    return sealLockedProps({ ...storedProps(props), lock } as ItemProps);
  }, [putRevealed]);

  const display = useCallback((item: CanvasItem): CanvasItem => {
    if (item.props.kind === "stack") {
      const collapsed = !(isLockedFolder(item) ? keys : expanded).has(item.id);
      return item.props.collapsed === collapsed ? item : { ...item, props: { ...item.props, collapsed } };
    }
    const entry = revealed.get(item.id);
    return entry && item.parentStackId === entry.folderId ? { ...item, props: entry.props } : item;
  }, [expanded, keys, revealed]);

  const storedItem = useCallback((itemId: string) => itemsRef.current.find((item) => item.id === itemId), []);
  const openFolderIds = useMemo(() => new Set([...keys.keys(), ...expanded]), [expanded, keys]);

  return useMemo(
    () => ({ openFolderIds, display, storedItem, openFolder, expandFolder, closeFolder, lockFolder, removeFolderLock, resealForSave }),
    [closeFolder, display, expandFolder, lockFolder, openFolder, openFolderIds, removeFolderLock, resealForSave, storedItem]
  );
}
